/**
 * The pricing cards every selling surface shows (landing page and /pricing), built for one language.
 *
 * Structure and prices come once from `PLANS` (landing/content.ts); names, blurbs and features from
 * getMyCIOPlans(language). Prices are numbers in USD formatted for the member's number locale, so a German
 * card reads "499 $" and never has a "$499" baked into its text. Pure, so it is unit-tested without React.
 */
import { getMyCIOPlans } from '@/lib/mycio/translations'
import { formatCurrency } from '@/lib/mycio/numberFormat'
import { PLANS } from '../landing/content'
import type { PricingPlan } from './PricingGrid'

const fill = (template: string, values: Record<string, string>) =>
  template.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? values[k] : m))

export function buildPricingPlans(language: string | null | undefined, numberLocale: string): PricingPlan[] {
  const plansCopy = getMyCIOPlans(language)
  const usd = (n: number | undefined) => (n == null ? '' : formatCurrency(n, { currency: 'USD', locale: numberLocale }))
  const planOrder = ['free', 'living', 'plus', 'wealth', 'crossBorder'] as const
  return PLANS.map((p, index) => {
    const tier = planOrder[index] ?? 'free'
    const copyKey = (p.id in plansCopy ? p.id : tier) as (typeof planOrder)[number]
    const c = plansCopy[copyKey] ?? plansCopy.free
    const values = { monthly: usd(p.amountUsd), annual: usd(p.annualUsd), renewal: usd(p.renewalUsd) }
    const price = p.billing === 'free' ? plansCopy.freePrice
      : p.billing === 'contact' ? fill(plansCopy.fromPrice, { price: usd(p.amountUsd) })
      // A renewal tier leads with the actual annual charge; the monthly equivalent is the note underneath.
      : p.billing === 'renewal' && p.annualUsd != null ? `${usd(p.annualUsd)}${plansCopy.perYear ?? ''}`
      : usd(p.amountUsd)
    return {
      id: p.id,
      name: c.name,
      blurb: c.blurb,
      price,
      per: p.billing === 'recurring' ? plansCopy.perMonth
        : p.billing === 'one_time' ? (plansCopy.once ?? '') : '',
      subNote: p.billing === 'renewal' && p.annualUsd != null
        ? fill(plansCopy.equivalentMonthly, { price: usd(p.amountUsd) })
        : undefined,
      cta: c.cta,
      features: c.features.map((f) => fill(f, values)),
      featured: p.featured,
      checkoutPlan: p.checkoutPlan,
    }
  })
}
